import { useCallback, useEffect } from 'react'

import styled from 'styled-components'

import { useLocation } from '@redwoodjs/router'

import AIAssistantPill from 'src/components/AIAssistantPill'
import {
  CitizenAIAssistantProvider,
  useCitizenAIAssistant,
} from 'src/components/CitizenAIAssistant/CitizenAIAssistantContext'
import { useIsGov } from 'src/layouts/AppStateContextLayout/utils'
import CitizenAIAssistantOverlay, {
  CitizenAIAssistantOverlayContent,
} from 'src/pages/CitizenAIAssistantPage/CitizenAIAssistantPage'
import { KeyboardKey } from 'src/types'
import {
  useIsInProductAICAEnabled,
  useIsInternalStaffAIEnabled,
} from 'src/utils/featureFlagging/featureFlagging'
import { LayoutMode, useSidebarLayoutMode } from 'src/utils/layout'

import { StylePropsMobile } from '../../components/shared/StyledComponents'
import { Sidebar } from '../../components/Sidebar/Sidebar'
import {
  StaffAIAssistantProvider,
  useStaffAIAssistant,
} from '../../components/StaffAIAssistant/StaffAIAssistantContext'
import TopBar from '../../components/Topbar/Topbar'
import useDisclosure from '../../hooks/use-disclosure'
import StaffAIAssistantOverlay, {
  StaffAIAssistantOverlayContent,
} from '../../pages/StaffAIAssistantPage/StaffAIAssistantPage'

type Props = {
  children?: React.ReactNode
}

const Right = styled.div<StylePropsMobile>`
  flex: 1;
  align-items: center;
  display: flex;
  flex-direction: column;
  transition: width 200ms ease-out;
  overflow-x: hidden;
  height: 100dvh;

  @media print {
    height: auto;
  }
`

const Content = styled.div<StylePropsMobile>`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
  background-color: ${({ theme }) => theme.colorWhite};
  overflow: hidden;
  position: relative;
  transition: all 300ms ease-in-out;

  ${({ $small }) =>
    !$small &&
    `
      border-top-left-radius: 8px;
      border-top-right-radius: 8px;
    `}
`

const ContentContainer = styled.div`
  height: 100%;

  @media print {
    height: auto;
  }
`

const SidebarLayout = (props: Props) => {
  return (
    <StaffAIAssistantProvider>
      <CitizenAIAssistantProvider>
        <SidebarLayoutInner {...props} />
      </CitizenAIAssistantProvider>
    </StaffAIAssistantProvider>
  )
}

const SidebarLayoutInner = (props: Props) => {
  const { children } = props
  const { layoutMode } = useSidebarLayoutMode()
  const isTopbarLayout = layoutMode === LayoutMode.Topbar
  const hamburgerMenuState = useDisclosure()
  const isGov = useIsGov()

  const isInternalStaffAIEnabled = useIsInternalStaffAIEnabled()
  const isInProductAICAEnabled = useIsInProductAICAEnabled(isGov)

  const staffAIContext = useStaffAIAssistant()
  const citizenAIContext = useCitizenAIAssistant()

  const isStaffAIOverlayOpen = staffAIContext?.overlay.isOpen ?? false
  const isCitizenAIOverlayOpen = citizenAIContext?.overlay.isOpen ?? false
  const isAIOverlayOpen = isStaffAIOverlayOpen || isCitizenAIOverlayOpen

  const isStaffSideViewOpen = isStaffAIOverlayOpen && staffAIContext?.viewMode === 'side'
  const isCitizenSideViewOpen = isCitizenAIOverlayOpen && citizenAIContext?.viewMode === 'side'
  const isSideViewOpen = isStaffSideViewOpen || isCitizenSideViewOpen

  const isStaffAIAvailable =
    isGov && isInternalStaffAIEnabled && !!staffAIContext && !isStaffAIOverlayOpen
  const isCitizenAIAvailable =
    !isGov && isInProductAICAEnabled && !!citizenAIContext && !isCitizenAIOverlayOpen

  const showFloatingPill = !isTopbarLayout && (isStaffAIAvailable || isCitizenAIAvailable)

  const topbarAIOnOpen = useCallback(() => {
    hamburgerMenuState.close()
    if (isStaffAIAvailable) {
      staffAIContext?.openFullView()
    } else if (isCitizenAIAvailable) {
      citizenAIContext?.openFullView()
    }
  }, [
    hamburgerMenuState,
    isStaffAIAvailable,
    isCitizenAIAvailable,
    staffAIContext,
    citizenAIContext,
  ])

  const showInlineAIInTopbar = isTopbarLayout && (isStaffAIAvailable || isCitizenAIAvailable)

  const { pathname } = useLocation()
  useEffect(() => {
    hamburgerMenuState.close()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  useEffect(() => {
    if (layoutMode !== LayoutMode.Topbar) {
      hamburgerMenuState.close()
    }
  }, [hamburgerMenuState, layoutMode])

  useEffect(() => {
    if (isTopbarLayout && isAIOverlayOpen) {
      hamburgerMenuState.close()
    }
  }, [isTopbarLayout, isAIOverlayOpen, hamburgerMenuState])

  useEffect(() => {
    if (!isSideViewOpen) {
      return
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === KeyboardKey.Escape) {
        if (isStaffSideViewOpen) {
          staffAIContext?.overlay.close()
        } else if (isCitizenSideViewOpen) {
          citizenAIContext?.overlay.close()
        }
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isSideViewOpen, isStaffSideViewOpen, isCitizenSideViewOpen, staffAIContext, citizenAIContext])

  return (
    <>
      <div className="bg-layout flex h-dvh max-w-screen">
        {!isTopbarLayout && <Sidebar layoutMode={layoutMode} />}
        <Right $small={isTopbarLayout}>
          {isTopbarLayout && (
            <TopBar
              onClickHamburger={hamburgerMenuState.toggle}
              hamburgerOpen={hamburgerMenuState.isOpen}
              aiAssistantOnOpen={showInlineAIInTopbar ? topbarAIOnOpen : undefined}
            />
          )}
          {/* CSS `hidden` instead of conditional rendering so `data-staff-ai-overlay-target`
              stays in the DOM. ContentAreaOverlay portals into that element; if it's unmounted
              when the AI overlay opens from the hamburger sidebar, the portal finds no target
              and the overlay never appears. */}
          <div
            className={`flex flex-1 self-stretch overflow-hidden transition-[margin] duration-300 ease-in-out ${
              hamburgerMenuState.isOpen ? 'hidden' : ''
            } ${
              !isTopbarLayout
                ? `mr-2.5 gap-2.5 ${isAIOverlayOpen ? 'mt-2.5' : showFloatingPill ? 'mt-9' : 'mt-2.5'}`
                : ''
            }`}
          >
            <Content
              $small={isTopbarLayout}
              className="border border-neutral-300"
              data-staff-ai-overlay-target
            >
              <ContentContainer>{children}</ContentContainer>
            </Content>
            {isStaffSideViewOpen && staffAIContext && (
              <div className="animate-in slide-in-from-right flex w-[400px] shrink-0 flex-col overflow-hidden rounded-tl-lg rounded-tr-lg border border-neutral-300 bg-white duration-300">
                <StaffAIAssistantOverlayContent
                  onClose={staffAIContext.overlay.close}
                  viewMode="side"
                  onMaximize={staffAIContext.maximize}
                  onMinimize={staffAIContext.minimize}
                />
              </div>
            )}
            {isCitizenSideViewOpen && citizenAIContext && (
              <div className="animate-in slide-in-from-right flex w-[400px] shrink-0 flex-col overflow-hidden rounded-tl-lg rounded-tr-lg border border-neutral-300 bg-white duration-300">
                <CitizenAIAssistantOverlayContent
                  onClose={citizenAIContext.overlay.close}
                  viewMode="side"
                  onMaximize={citizenAIContext.maximize}
                  onMinimize={citizenAIContext.minimize}
                />
              </div>
            )}
          </div>
          {hamburgerMenuState.isOpen && <Sidebar layoutMode={layoutMode} />}
        </Right>
      </div>
      {showFloatingPill && isStaffAIAvailable && (
        <AIAssistantPill onOpen={staffAIContext?.openSideView} />
      )}
      {showFloatingPill && isCitizenAIAvailable && (
        <AIAssistantPill onOpen={citizenAIContext?.openSideView} />
      )}
      <StaffAIAssistantOverlay />
      <CitizenAIAssistantOverlay />
    </>
  )
}

export default SidebarLayout
